import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock, LockOpen, ShieldAlert, ShieldCheck, Search, Loader2, RefreshCw, History, FileSearch } from "lucide-react";

const fmtTs = (s) => (s ? new Date(s).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

const ESTADO_REG = {
  abierto: { label: "Abierto", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: LockOpen },
  cerrado: { label: "Cerrado", cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600", Icon: Lock },
  incierto: { label: "Pendiente de revisión", cls: "bg-amber-500/15 text-amber-200 border-amber-500/40", Icon: ShieldAlert },
};
const ESTADO_SYNC = {
  sincronizado: { label: "Sincronizado", cls: "text-emerald-300" },
  no_enviado: { label: "No enviado", cls: "text-zinc-400" },
  incierto: { label: "Incierto · reconciliar", cls: "text-amber-300" },
  enviando: { label: "Enviando / interrumpido · reconciliar", cls: "text-amber-300" },
};

export function EstadoRegistroBadge({ estado, testid }) {
  const cfg = ESTADO_REG[estado] || ESTADO_REG.incierto;
  return (
    <span data-testid={testid} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border inline-flex items-center gap-1 font-bold ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function MotivoDialog({ open, titulo, descripcion, confirmLabel, onClose, onConfirm, busy, danger }) {
  const [motivo, setMotivo] = useState("");
  useEffect(() => { if (open) setMotivo(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-zinc-950 border-zinc-800" data-testid="fd-ciclo-motivo-dialog">
        <DialogHeader><DialogTitle className="text-zinc-100">{titulo}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-400">{descripcion}</p>
        <textarea data-testid="fd-ciclo-motivo-input" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Motivo (obligatorio, mín. 3 caracteres)"
          className="w-full rounded border border-zinc-700 bg-zinc-900 text-sm text-zinc-100 p-2 focus:outline-none focus:border-amber-500/60" />
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="fd-ciclo-motivo-cancelar">Cancelar</Button>
          <Button disabled={busy || motivo.trim().length < 3} onClick={() => onConfirm(motivo.trim())} data-testid="fd-ciclo-motivo-confirmar"
            className={danger ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-zinc-950"}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegistroFila({ r, onAccion }) {
  const snap = r.snapshot || {};
  return (
    <tr className="border-b border-zinc-800/60 hover:bg-zinc-800/30" data-testid={`fd-ciclo-fila-${r.id}`}>
      <td className="px-3 py-2.5 text-zinc-200">{r.cliente_nombre || r.cliente_id}</td>
      <td className="px-3 py-2.5 font-mono text-amber-300">{r.fd_delivery_note_number || r.fd_delivery_note_id?.slice(0, 8)}{r.aparte && <span className="ml-1 text-[10px] text-zinc-500">(aparte)</span>}</td>
      <td className="px-3 py-2.5"><EstadoRegistroBadge estado={r.estado} testid={`fd-ciclo-estado-${r.id}`} /></td>
      <td className="px-3 py-2.5 text-xs text-zinc-300">{snap.n_lineas ?? "—"} líneas · {r.n_albaranes_locales} ALB</td>
      <td className="px-3 py-2.5 text-xs text-zinc-400">{fmtTs(r.updated_at)}{r.cierre && <div className="text-zinc-500">Cerrado por {r.cierre.usuario} · {fmtTs(r.cierre.fecha)}</div>}{r.motivo_incierto && <div className="text-amber-300/80">{r.motivo_incierto}</div>}</td>
      <td className="px-3 py-2.5 text-right">
        <div className="inline-flex gap-1">
          {r.estado !== "cerrado" && (
            <Button size="sm" onClick={() => onAccion("cerrar", r)} data-testid={`fd-ciclo-cerrar-${r.id}`} className="h-7 px-2 text-[11px] bg-zinc-800 hover:bg-rose-600 text-zinc-100 border border-zinc-700">
              <Lock className="w-3 h-3 mr-1" /> Cerrar
            </Button>
          )}
          {r.estado !== "abierto" && (
            <Button size="sm" variant="outline" onClick={() => onAccion("confirmar", r)} data-testid={`fd-ciclo-confirmar-${r.id}`} className="h-7 px-2 text-[11px] bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15">
              <ShieldCheck className="w-3 h-3 mr-1" /> Confirmar abierto
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Trazabilidad() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const buscar = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try { const { data } = await api.get("/fd-albaranes/trazabilidad", { params: { q: q.trim() } }); setRes(data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error buscando"); }
    finally { setBusy(false); }
  };
  const reconciliar = async (aid) => {
    try { const { data } = await api.post(`/fd-albaranes/albaran/${aid}/reconciliar`); toast[data.resultado === "incierto" ? "warning" : "success"](`${data.resultado}: ${data.mensaje}`); buscar(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error reconciliando"); }
  };
  return (
    <div className="space-y-3" data-testid="fd-trazabilidad">
      <form onSubmit={buscar} className="flex gap-2">
        <input data-testid="fd-trazabilidad-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nº albarán (ALB-…), nº FD (A-26-…), código petición (DNF-…), referencia cliente, id de línea…"
          className="flex-1 rounded border border-zinc-700 bg-zinc-900 text-sm text-zinc-100 px-3 py-1.5 focus:outline-none focus:border-amber-500/60" />
        <Button type="submit" disabled={busy} data-testid="fd-trazabilidad-buscar" className="bg-amber-500 hover:bg-amber-400 text-zinc-950 h-8">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-3.5 h-3.5 mr-1" /> Buscar</>}</Button>
      </form>
      {res && (
        <div className="space-y-2">
          <div className="text-xs text-zinc-500" data-testid="fd-trazabilidad-total">{res.total} resultado(s) para «{res.q}»</div>
          {res.items.map((it) => {
            const es = ESTADO_SYNC[it.fd_sync_estado] || ESTADO_SYNC.no_enviado;
            return (
              <div key={it.albaran_local_id} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-xs space-y-1.5" data-testid={`fd-traza-${it.numero}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-amber-300 font-semibold">{it.numero}</span>
                  <span className="text-zinc-300">{it.cliente}</span>
                  <span className="text-zinc-500">{it.fecha_emision} · {eur(it.total)} · {it.origen}</span>
                  <span className={`ml-auto font-semibold ${es.cls}`} data-testid={`fd-traza-sync-${it.numero}`}>{es.label}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-zinc-400">
                  <span>FD: <b className="text-zinc-200 font-mono">{it.fd_delivery_note_number || "—"}</b>{it.fd_sync_action ? ` (${it.fd_sync_action})` : ""}</span>
                  {it.registro_ciclo && <span>Ciclo {it.registro_ciclo.periodo}: <EstadoRegistroBadge estado={it.registro_ciclo.estado} /></span>}
                  {it.origen_peticion_id && <a className="text-indigo-300 hover:underline" href={`/peticiones/${it.origen_peticion_id}`}>Petición</a>}
                  {it.origen_ruta_id && <a className="text-indigo-300 hover:underline" href={`/rutas/${it.origen_ruta_id}`}>Ruta · parada {(it.origen_parada_idx ?? 0) + 1}</a>}
                </div>
                {it.fd_sync_error && <div className="text-amber-300/90">{it.fd_sync_error}</div>}
                {it.ultimo_intento && <div className="text-zinc-500"><History className="w-3 h-3 inline mr-1" />Último intento: {it.ultimo_intento.accion} → <b>{it.ultimo_intento.resultado}</b> · {fmtTs(it.ultimo_intento.ts)} · {it.ultimo_intento.user || "—"}{it.ultimo_intento.error ? ` · ${it.ultimo_intento.error}` : ""}</div>}
                {["incierto", "enviando"].includes(it.fd_sync_estado) && (
                  <Button size="sm" onClick={() => reconciliar(it.albaran_local_id)} data-testid={`fd-traza-reconciliar-${it.numero}`} className="h-7 px-2 text-[11px] bg-amber-500 hover:bg-amber-400 text-zinc-950"><RefreshCw className="w-3 h-3 mr-1" /> Reconciliar con FD</Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FdCicloPanel() {
  const [periodo, setPeriodo] = useState("");
  const [periodos, setPeriodos] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogo, setDialogo] = useState(null); // {tipo, r}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/fd-albaranes/ciclo", { params: p ? { periodo: p } : {} });
      setData(d); setPeriodo(d.periodo);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error cargando ciclo FD"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); api.get("/fd-albaranes/periodos").then(({ data: d }) => setPeriodos(d.periodos)).catch(() => {}); }, [load]);

  const ejecutar = async (motivo) => {
    const { tipo, r } = dialogo;
    setBusy(true);
    try {
      const url = `/fd-albaranes/ciclo/${r.id}/${tipo === "cerrar" ? "cerrar" : "confirmar-abierto"}`;
      const { data: d } = await api.post(url, { motivo, confirmar: true });
      toast.success(d.mensaje);
      setDialogo(null); load(periodo);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const resumen = data?.resumen || {};
  return (
    <div className="space-y-4" data-testid="fd-ciclo-panel">
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-100 space-y-1" data-testid="fd-ciclo-aviso">
        <div className="flex items-start gap-2"><ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
          <div><b>Cierre de mes:</b> DNF solo añade montajes a un albarán FD que aquí figure <b>Abierto</b>. Antes de facturar un albarán en FacturaDirecta, <b>ciérralo aquí</b>: el siguiente montaje irá a un albarán nuevo.
            <div className="text-amber-200/70 mt-1">Límite: {data?.limite || "el estado local no detecta por sí solo una factura hecha directamente en FD."} Si FD cambia un albarán fuera de DNF, queda «Pendiente de revisión» y se bloquea hasta que lo confirmes o cierres.</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-zinc-400">Periodo</label>
        <select data-testid="fd-ciclo-periodo" value={periodo} onChange={(e) => load(e.target.value)} className="rounded border border-zinc-700 bg-zinc-900 text-sm text-zinc-100 px-2 py-1">
          {(periodos.length ? periodos : [periodo]).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="flex gap-2 text-[11px]" data-testid="fd-ciclo-resumen">
          <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{resumen.abierto || 0} abiertos</span>
          <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-200 border border-amber-500/30">{resumen.incierto || 0} pendientes de revisión</span>
          <span className="px-2 py-0.5 rounded bg-zinc-700/40 text-zinc-300 border border-zinc-600">{resumen.cerrado || 0} cerrados</span>
          {data?.albaranes_locales_inciertos > 0 && <span className="px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30" data-testid="fd-ciclo-alb-inciertos">{data.albaranes_locales_inciertos} albaranes locales por reconciliar</span>}
        </div>
        <Button size="sm" variant="ghost" onClick={() => load(periodo)} data-testid="fd-ciclo-refrescar" className="ml-auto h-7 text-zinc-400"><RefreshCw className="w-3.5 h-3.5" /></Button>
      </div>

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? <div className="flex items-center justify-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando…</div>
          : !data?.items?.length ? <div className="text-center py-12 text-zinc-500 text-sm" data-testid="fd-ciclo-vacio">Sin albaranes FD registrados en {periodo}. Se registran automáticamente al sincronizar el primer montaje del mes.</div>
          : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase text-zinc-500 border-b border-zinc-800">
                <th className="px-3 py-2.5">Cliente</th><th className="px-3 py-2.5">Albarán FD</th><th className="px-3 py-2.5">Estado</th><th className="px-3 py-2.5">Contenido</th><th className="px-3 py-2.5">Última actividad</th><th className="px-3 py-2.5 text-right">Acciones</th>
              </tr></thead>
              <tbody>{data.items.map((r) => <RegistroFila key={r.id} r={r} onAccion={(tipo, reg) => setDialogo({ tipo, r: reg })} />)}</tbody>
            </table>
          )}
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4 space-y-3">
        <div className="text-sm text-zinc-200 font-semibold inline-flex items-center gap-2"><FileSearch className="w-4 h-4 text-amber-300" /> Trazabilidad montaje / petición → albarán FD</div>
        <Trazabilidad />
      </div>

      <MotivoDialog open={!!dialogo} busy={busy} onClose={() => setDialogo(null)} onConfirm={ejecutar}
        danger={dialogo?.tipo === "cerrar"}
        titulo={dialogo?.tipo === "cerrar" ? `Cerrar albarán FD ${dialogo?.r?.fd_delivery_note_number || ""}` : `Confirmar ABIERTO ${dialogo?.r?.fd_delivery_note_number || ""}`}
        descripcion={dialogo?.tipo === "cerrar"
          ? "Se bloquea en DNF: ningún montaje más se añadirá a este albarán. El siguiente montaje del cliente creará un albarán FD nuevo (aunque sea este mismo mes). Los montajes ya sincronizados no se tocan ni se duplican. Hazlo ANTES de facturarlo en FacturaDirecta."
          : "Solo tras revisar el albarán en FacturaDirecta. Se toma un snapshot fresco (líneas, fecha de modificación) y vuelve a admitir montajes. Si ya está facturado en FD, NO lo confirmes: ciérralo."}
        confirmLabel={dialogo?.tipo === "cerrar" ? "Cerrar albarán" : "Confirmar abierto"} />
    </div>
  );
}
